"use client";

import { useEffect, useMemo } from "react";
import { Globe, ChevronDown } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import {
  setTimezone,
  initializeTimezone,
} from "@/redux/features/timezone/timezoneSlice";
import {
  TimezoneOption,
  BUSINESS_TIMEZONE,
  detectUserTimezone,
  getTimezoneDisplayName,
  getTimezoneAbbreviation,
  areTimezonesEqual,
} from "@/lib/timezone";

interface TimezoneSelectorProps {
  themeColor?: string;
  className?: string;
}

const TimezoneSelector = ({
  themeColor = "#006DFF",
  className = "",
}: TimezoneSelectorProps) => {
  const dispatch = useDispatch();
  const timezoneState = useSelector((state: RootState) => state.timezone);
  const selectedTimezone = timezoneState?.selectedTimezone ?? "local";
  const isInitialized = timezoneState?.isInitialized ?? false;

  // Initialize timezone on mount
  useEffect(() => {
    dispatch(initializeTimezone());
  }, [dispatch]);

  // Get user's detected timezone
  const userTimezone = useMemo(() => detectUserTimezone(), []);

  // Check if user's timezone is the same as business timezone
  const isSameAsBusinessTz = useMemo(
    () => areTimezonesEqual(userTimezone, BUSINESS_TIMEZONE),
    [userTimezone]
  );

  // Get display names
  const userTimezoneDisplay = useMemo(
    () => getTimezoneDisplayName(userTimezone),
    [userTimezone]
  );

  const businessTimezoneDisplay = useMemo(
    () => getTimezoneDisplayName(BUSINESS_TIMEZONE),
    []
  );

  // Current display value
  const currentDisplay = useMemo(() => {
    if (selectedTimezone === "local") {
      return `Your Time - ${userTimezoneDisplay}`;
    }
    return businessTimezoneDisplay;
  }, [selectedTimezone, userTimezoneDisplay, businessTimezoneDisplay]);

  // Short display for the selected value
  const shortDisplay = useMemo(() => {
    if (selectedTimezone === "local") {
      const abbrev = getTimezoneAbbreviation(userTimezone);
      return `Your Time (${abbrev})`;
    }
    const abbrev = getTimezoneAbbreviation(BUSINESS_TIMEZONE);
    return `Australia (${abbrev})`;
  }, [selectedTimezone, userTimezone]);

  const handleTimezoneChange = (value: TimezoneOption) => {
    dispatch(setTimezone(value));
  };

  // If user's timezone is same as business timezone, show simplified view
  if (isSameAsBusinessTz) {
    return (
      <div className={`flex flex-col gap-2 ${className}`}>
        <span className="font-onest text-lg font-bold text-black">
          Time zone
        </span>
        <div className="flex items-center gap-3">
          <Globe className="size-4 text-black" />
          <span className="font-onest text-base font-normal text-black">
            {businessTimezoneDisplay}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <span className="font-onest text-lg font-bold text-black">Time zone</span>
      <div className="relative">
        <div className="flex items-center gap-3">
          <Globe className="size-4 text-black" />
          <div className="relative flex-1">
            <select
              value={selectedTimezone}
              onChange={(e) =>
                handleTimezoneChange(e.target.value as TimezoneOption)
              }
              className="w-full cursor-pointer appearance-none bg-transparent pr-6 font-onest text-base font-normal text-black focus:outline-none"
              style={{ color: themeColor }}
            >
              <option value="local">Your Time - {userTimezoneDisplay}</option>
              <option value={BUSINESS_TIMEZONE}>{businessTimezoneDisplay}</option>
            </select>
            <ChevronDown
              className="pointer-events-none absolute right-0 top-1/2 size-4 -translate-y-1/2"
              style={{ color: themeColor }}
            />
          </div>
        </div>
      </div>
      {selectedTimezone === "local" && (
        <p className="font-onest text-xs text-black/50">
          Times will be shown in your local timezone. Business hours are in
          Australian Eastern Time.
        </p>
      )}
    </div>
  );
};

export default TimezoneSelector;

// Hook to get the current effective timezone
export function useSelectedTimezone() {
  const timezoneState = useSelector((state: RootState) => state.timezone);
  const selectedTimezone = timezoneState?.selectedTimezone ?? "local";
  const isInitialized = timezoneState?.isInitialized ?? false;
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(initializeTimezone());
  }, [dispatch]);

  const effectiveTimezone = useMemo(() => {
    if (selectedTimezone === "local") {
      return detectUserTimezone();
    }
    return selectedTimezone;
  }, [selectedTimezone]);

  return {
    selectedTimezone,
    effectiveTimezone,
    isInitialized,
    isLocalTimezone: selectedTimezone === "local",
    isBusinessTimezone: selectedTimezone === BUSINESS_TIMEZONE,
  };
}
